import { motion } from 'framer-motion'
import { memo } from 'react'

/**
 * Enhanced Skeleton Component
 * Provides better loading animations and patterns for dashboard
 */
const EnhancedSkeleton = memo(({ 
  className = '', 
  variant = 'default',
  lines = 1,
  width = '100%',
  height = 'auto',
  animate = true,
  ...props 
}) => {
  const baseClasses = 'bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 rounded'
  
  const variants = {
    default: 'h-4',
    text: 'h-3',
    title: 'h-6',
    avatar: 'w-10 h-10 rounded-full',
    card: 'h-24 rounded-xl',
    button: 'h-8 w-20 rounded-lg',
    image: 'w-full h-32 rounded-xl'
  }

  const skeletonAnimation = animate ? {
    initial: { opacity: 0.6 },
    animate: { 
      opacity: [0.6, 1, 0.6],
      backgroundPosition: ['200% 0', '-200% 0']
    },
    transition: { 
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  } : {}

  if (lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }, (_, i) => (
          <motion.div
            key={i}
            className={`${baseClasses} ${variants[variant] || variants.default} ${className}`}
            style={{ 
              width: i === lines - 1 ? '75%' : width,
              height,
              backgroundSize: '400% 100%'
            }}
            {...skeletonAnimation}
            {...props}
          />
        ))}
      </div>
    )
  }

  return (
    <motion.div
      className={`${baseClasses} ${variants[variant] || variants.default} ${className}`}
      style={{ 
        width, 
        height,
        backgroundSize: '400% 100%'
      }}
      {...skeletonAnimation}
      {...props}
    />
  )
})

// Preset skeleton components for common use cases
export const TextSkeleton = (props) => <EnhancedSkeleton variant="text" {...props} />
export const TitleSkeleton = (props) => <EnhancedSkeleton variant="title" {...props} />
export const AvatarSkeleton = (props) => <EnhancedSkeleton variant="avatar" {...props} />
export const CardSkeleton = (props) => <EnhancedSkeleton variant="card" {...props} />
export const ButtonSkeleton = (props) => <EnhancedSkeleton variant="button" {...props} />
export const ImageSkeleton = (props) => <EnhancedSkeleton variant="image" {...props} />

// Dashboard-specific skeleton components
export const DashboardStatsSkeleton = memo(() => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="p-3 sm:p-4 rounded-xl border border-neutral-200">
        <div className="flex items-center justify-between">
          <div className="flex-1 space-y-2">
            <EnhancedSkeleton variant="text" width="60%" />
            <EnhancedSkeleton variant="title" width="80%" />
            <EnhancedSkeleton variant="text" width="40%" />
          </div>
          <EnhancedSkeleton variant="avatar" />
        </div>
      </div>
    ))}
  </div>
))

export const DashboardProjectsSkeleton = memo(() => (
  <div className="space-y-3 sm:space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-neutral-200">
        <EnhancedSkeleton className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <EnhancedSkeleton variant="title" width="75%" />
          <EnhancedSkeleton variant="text" width="50%" />
          <div className="flex items-center gap-2">
            <EnhancedSkeleton width="60px" height="20px" className="rounded-full" />
            <EnhancedSkeleton width="40px" height="16px" />
          </div>
        </div>
        <EnhancedSkeleton variant="button" />
      </div>
    ))}
  </div>
))

EnhancedSkeleton.displayName = 'EnhancedSkeleton'
DashboardStatsSkeleton.displayName = 'DashboardStatsSkeleton'
DashboardProjectsSkeleton.displayName = 'DashboardProjectsSkeleton'

export default EnhancedSkeleton